import { Auth, CognitoHostedUIIdentityProvider } from '@aws-amplify/auth'
import { Hub } from '@aws-amplify/core'
import { authConfig } from '@config/auth'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'
import type { TokenService } from 'shared-ui/api-provider'
import { AuthProvider, type AuthState, AuthStatus, type User } from 'shared-ui/auth-provider'
import { useDebug } from 'shared-ui/debug'

import { ActiveOperationProvider } from './ActiveOperationProvider'

/**
 * TODO:
 *
 * Note that we had to revert to amplify v5 because v6 does not support direct link to sign up page.
 * The v5 code here is a workaround, and was a hack even before, but at least it worked.
 * If v6 ever supports our use case, we should upgrade. We may also consider doing this logic ourselves,
 * but this will require storing refresh tokens, refreshing them, reading the code from the url, etc.
 * After mobile has implemented this logic, I think we should consider borrowing the logic here and
 * ditching amplify entirely.
 *
 * See also: https://github.com/aws-amplify/amplify-js/issues/12282
 */

Auth.configure(authConfig)

const getUser = Auth.currentAuthenticatedUser.bind(Auth)

// if this is in the react component, then the listener isn't registered in time
// the signIn event happens nearly immediately upon page load
Hub.listen('auth', ({ payload: { event, data } }) => {
  if (event !== 'signIn_failure' || !(data instanceof Error)) {
    return
  }

  alert('Authentication failed. ' + data.message.split('+').join(' '))
})

export const AuthWrapper: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const redirectUrl =
    (location.state as { redirectUrl?: string } | null)?.redirectUrl ??
    location.pathname + location.search
  const logoutChannel = useMemo(() => new BroadcastChannel('logout'), [])
  const { logError } = useDebug()

  const [state, setState] = useState<AuthState>({
    status: AuthStatus.Unknown,
    user: null
  })

  useEffect(() => {
    const unlisten = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signIn':
          break

        case 'signedOut':
          break

        case 'customOAuthState':
          navigate(payload.data as string, { replace: true })
          break
      }
    })
    return () => unlisten()
  }, [navigate])

  useEffect(() => {
    let cancel = false

    getUser()
      .then(async (user: User) => {
        if (cancel) return
        setState({ user, status: AuthStatus.Authenticated })
      })
      .catch(async (_err) => {
        if (cancel || state.status === AuthStatus.Unauthenticated) return
        setState({ user: null, status: AuthStatus.Unauthenticated })
      })

    return () => {
      cancel = true
    }
  }, [redirectUrl, state.status, logError])

  const signOut = useCallback(async () => {
    logoutChannel.postMessage('logged-out')
    setState({ status: AuthStatus.Unauthenticated, user: null })
    await Auth.signOut()
  }, [logoutChannel])

  const forceSignIn = async () => {
    setState({ status: AuthStatus.Authenticating, user: null })
    await Auth.federatedSignIn({
      customState: redirectUrl,
      customProvider: CognitoHostedUIIdentityProvider.Cognito
    })
  }

  const signUp = async () => {
    const configDirectToSignUp = {
      ...authConfig,
      oauth: {
        ...authConfig.oauth,
        urlOpener: async (url: string) =>
          window.open(url.replace(/\/oauth2\/authorize\?/, '/signup?'), '_self')
      }
    }

    Auth.configure(configDirectToSignUp)
    return forceSignIn()
  }

  useEffect(() => {
    const handler = () => {
      setState({ status: AuthStatus.Unauthenticated, user: null })
      logoutChannel.close()
    }
    logoutChannel.addEventListener('message', handler)
    return () => {
      logoutChannel.removeEventListener('message', handler)
    }
  }, [logoutChannel])

  const value = {
    user: state.user,
    status: state.status,
    signOut,
    forceSignIn,
    signUp
  }

  return (
    <AuthProvider value={value}>
      <ActiveOperationProvider>
        <Outlet />
      </ActiveOperationProvider>
    </AuthProvider>
  )
}

export { AuthStatus, useAuth } from 'shared-ui/auth-provider'

export const tokenService: TokenService = {
  async getAccessToken() {
    const session = await Auth.currentSession()
    return session.getAccessToken().getJwtToken()
  }
}

/**
 * Resend the sign up confirmation code
 */
export const resendSignUp = async (email: string) => {
  await Auth.resendSignUp(email)
}

export const confirmSignUp = async (email: string, code: string) => {
  await Auth.confirmSignUp(email, code)
}
